<script setup lang="ts">
import { onMounted, ref, watchEffect, defineOptions } from "vue";
import { fetchChineseCalendarMonth } from "@/api/calculators";
import { ChineseCalendarDate } from "@/types/calculators";
import { formatLocalDate } from "@/utils/date";
import {
  CHINESE_CALENDAR_SUPPORTED_YEARS,
  MONTHS_KEYS,
} from "@/constants/chineseCalendar";
import { useI18n } from "vue-i18n";
import AppSelect from "@/components/inputs/AppSelect.vue";
import MobileCalendar from "@/views/Calculators/ChineseCalendar/components/MobileCalendar.vue";
import DescktopCalendar from "@/views/Calculators/ChineseCalendar/components/DescktopCalendar.vue";
import PageTitleWithBackButton from "@/components/PageTitleWithBackButton.vue";
import CheckBox from "@/components/inputs/CheckBox.vue";
import PrintButton from "@/components/buttons/PrintButton.vue";

const currentYear = ref<number | null>(null);
const currentMonth = ref<number | null>(null);
const chineseYear = ref(0);
const chineseMonth = ref(0);
const chineseCalendar = ref<ChineseCalendarDate[]>([]);
const showAsIcons = ref<boolean>(false);
const waitApiCall = ref<boolean>(false);

const desktopCalendarRef = ref();

const { t } = useI18n();

defineOptions({
  name: "ChineseCalendarView",
});

onMounted(() => {
  const now = new Date();
  currentYear.value = now.getFullYear();
  currentMonth.value = now.getMonth();
});

watchEffect(async () => {
  if (currentYear.value === null || currentMonth.value === null) {
    return;
  }
  chineseCalendar.value = [];
  await getChineseCalendar();
});

async function getChineseCalendar() {
  waitApiCall.value = true;
  if (currentYear.value === null || currentMonth.value === null) {
    return;
  }
  try {
    chineseCalendar.value = await fetchChineseCalendarMonth(
      formatLocalDate(new Date(currentYear.value, currentMonth.value, 1)),
      formatLocalDate(new Date(currentYear.value, currentMonth.value + 1, 0))
    );
    chineseYear.value = currentYear.value;
    chineseMonth.value = currentMonth.value;
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <div class="h-full xl:h-screen w-full pt-6 xl:pt-[88px] flex flex-col">
    <!-- CONTENT CONTAINER -->
    <div
      class="w-full max-md:h-full md:flex-1 md:overflow-y-auto no-scrollbar py-6 px-4 md:px-[20px] flex flex-col md:items-center gap-6"
    >
      <!-- TITLE WITH BACK BUTTON -->
      <PageTitleWithBackButton
        :page-title="t('calculators.chinese-calendar.title')"
        :title-align="'left'"
        class="md:w-full md:max-w-[1161px] xl:!flex !mb-0"
      />
      <!-- MOBILE CHINESE CALENDAR -->
      <MobileCalendar
        class="md:hidden"
        v-if="chineseCalendar"
        :chinese-calendar="chineseCalendar"
        :month="chineseMonth"
        :year="chineseYear"
        :month-key="MONTHS_KEYS[chineseMonth]"
      />
      <!-- ACTIONS -->
      <div
        class="grid grid-cols-2 md:grid-cols-[auto_auto_1fr_1fr] gap-6 md:w-full md:max-w-[1161px]"
      >
        <AppSelect
          class="md:w-[132px]"
          :items="CHINESE_CALENDAR_SUPPORTED_YEARS"
          :default-value="currentYear"
          @select-option="(selectedYear: number) => currentYear = Number(selectedYear)"
        />
        <AppSelect
          class="md:w-[164px]"
          :items="MONTHS_KEYS"
          :default-value="MONTHS_KEYS[currentMonth]"
          @select-option="(selectedMonth: string) => currentMonth = MONTHS_KEYS.indexOf(selectedMonth)"
        >
          <template #option="{ item }">
            {{ t(`months.${item}`) }}
          </template>
        </AppSelect>
        <div class="col-span-2 flex gap-[12px] md:gap-6">
          <PrintButton
            v-if="false"
            btn-classes="size-11"
            icon-classes="mx-auto"
            @click="handlePrint"
          />
          <CheckBox
            input-id="show-icons"
            label="Показывать иконками"
            @toggle="showAsIcons = !showAsIcons"
            class="select-none"
          />
        </div>
      </div>
      <!-- DESKTOP CHINESE CALENDAR -->
      <DescktopCalendar
        ref="desktopCalendarRef"
        class="max-md:hidden"
        v-if="chineseCalendar"
        :chinese-calendar="chineseCalendar"
        :month="chineseMonth"
        :year="chineseYear"
        :month-key="MONTHS_KEYS[chineseMonth]"
        :showAsIcons="showAsIcons"
      />
    </div>
  </div>
</template>
